import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon, type IconName } from "../components/Icon";
import type { PatchDocument } from "../domain/patch";
import { getNormalledRouteStates, traceSignal, validateSignalPatch } from "../domain/signal-graph";
import { instrumentBundleForDocument, instrumentCatalog } from "../instruments/catalog";
import { usePatchPersistence } from "../persistence/usePatchPersistence";
import { loadAttachmentBinary } from "../persistence/database";
import { PrintPreview } from "../print/PrintPreview";
import {
  applyApplicationSettings,
  loadApplicationSettings,
} from "../settings/application-settings";
import { DataPortabilityDialog, type DataDialogMode } from "../transfer/DataPortability";
import { WorkspaceCanvas } from "../workspace/WorkspaceCanvas";
import { CableManager } from "../workspace/CableManager";
import { ExternalEndpointManager } from "../workspace/ExternalEndpointManager";
import { UtilityManager } from "../workspace/UtilityManager";
import { PatchListView } from "../workspace/PatchListView";
import { RecallDock } from "../workspace/RecallDock";
import { compiledInstrumentDefinitionForDocument } from "../workspace/workspace-ports";
import { useEditorStore, type PatchMetadataUpdate, type ToolMode, type ViewMode } from "./store";
import { AttachmentsInspector, NotesInspector } from "./InspectorDocumentation";

const DesignStudio = lazy(async () => {
  const module = await import("../synth-designer/studio/DesignStudio");
  return { default: module.DesignStudio };
});

type ApplicationMode = "patch" | "design";

function Mark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <svg viewBox="0 0 32 32">
        <path d="M9 16a7 7 0 1 1 7 7h-2" />
        <path d="M14 8v8h8M13 24h5M12 27h7" />
      </svg>
    </span>
  );
}

function ToolbarButton({
  icon,
  label,
  compact = false,
  disabled = false,
  onClick,
}: {
  icon: IconName;
  label: string;
  compact?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      aria-label={label}
      className={`header-action${compact ? " compact" : ""}`}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      <Icon name={icon} size={16} />
      {!compact && <span>{label}</span>}
    </button>
  );
}

function AppHeader({
  applicationMode,
  onExport,
  onImport,
  onModeChange,
  onNavigateAway,
  onNewPatch,
  onPrint,
  onSettings,
}: {
  applicationMode: ApplicationMode;
  onExport: () => void;
  onImport: () => void;
  onModeChange: (mode: ApplicationMode) => void;
  onNavigateAway: () => boolean;
  onNewPatch: () => void;
  onPrint: () => void;
  onSettings: () => void;
}) {
  const {
    activeDocument,
    closePatch,
    leftPanelOpen,
    inspectorOpen,
    openPatch,
    openPatchIds,
    patchLibrary,
    persistenceStatus,
    requestSave,
    toggleLeftPanel,
    toggleInspector,
  } = useEditorStore();
  return (
    <header className="app-header">
      <div className="brand-area">
        <button
          aria-label={leftPanelOpen ? "Hide patch library" : "Show patch library"}
          className="icon-button mobile-panel-toggle"
          onClick={toggleLeftPanel}
          type="button"
        >
          <Icon name="menu" />
        </button>
        <Mark />
        <div className="brand-copy">
          <strong>Patchwork</strong>
          <span>Synth notebook</span>
        </div>
      </div>

      <nav aria-label="Open patches" className="document-tabs">
        {openPatchIds.map((patchId) => {
          const patch = patchLibrary.find((item) => item.id === patchId);
          if (!patch) return null;
          const active = patchId === activeDocument.id;
          return (
            <div className={`document-tab${active ? " is-active" : ""}`} key={patchId}>
              <button
                aria-current={active ? "page" : undefined}
                className="document-tab-main"
                onClick={() => {
                  if (active || onNavigateAway()) void openPatch(patchId);
                }}
                type="button"
              >
                <span className="tab-status" />
                <span className="tab-copy">
                  <strong>{patch.title}</strong>
                  <small>{patch.instrument}</small>
                </span>
              </button>
              {openPatchIds.length > 1 && (
                <button
                  aria-label={`Close ${patch.title}`}
                  className="document-tab-close"
                  onClick={() => {
                    if (!active || onNavigateAway()) void closePatch(patchId);
                  }}
                  type="button"
                >
                  <Icon name="close" size={13} />
                </button>
              )}
            </div>
          );
        })}
        <button
          aria-label="New patch"
          className="new-tab"
          onClick={() => {
            if (onNavigateAway()) onNewPatch();
          }}
          type="button"
        >
          <Icon name="plus" size={17} />
        </button>
      </nav>

      <div aria-label="Application mode" className="application-mode-switch" role="group">
        <button
          aria-pressed={applicationMode === "patch"}
          className={applicationMode === "patch" ? "is-active" : ""}
          onClick={() => onModeChange("patch")}
          type="button"
        >
          Patch
        </button>
        <button
          aria-pressed={applicationMode === "design"}
          className={applicationMode === "design" ? "is-active" : ""}
          onClick={() => onModeChange("design")}
          type="button"
        >
          Design
        </button>
      </div>

      <div className="header-actions">
        {applicationMode === "patch" && (
          <>
            <ToolbarButton
              disabled={persistenceStatus === "loading" || persistenceStatus === "saving"}
              icon="save"
              label="Save now"
              onClick={requestSave}
            />
            <ToolbarButton icon="import" label="Import" onClick={onImport} />
            <ToolbarButton icon="export" label="Export" onClick={onExport} />
            <ToolbarButton icon="print" label="Print" onClick={onPrint} />
          </>
        )}
        <ToolbarButton compact icon="settings" label="Settings" onClick={onSettings} />
        <button
          aria-label={inspectorOpen ? "Hide inspector" : "Show inspector"}
          className="icon-button mobile-panel-toggle"
          onClick={toggleInspector}
          type="button"
        >
          <Icon name="panelRight" />
        </button>
      </div>
    </header>
  );
}

function useAttachmentUrl(attachmentId?: string) {
  const [loaded, setLoaded] = useState<{ id: string; url: string } | null>(null);
  useEffect(() => {
    if (!attachmentId) return;
    let current = true;
    let objectUrl: string | undefined;
    void loadAttachmentBinary(attachmentId).then((blob) => {
      if (!blob || !current) return;
      objectUrl = URL.createObjectURL(blob);
      setLoaded({ id: attachmentId, url: objectUrl });
    });
    return () => {
      current = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachmentId]);
  return loaded && loaded.id === attachmentId ? loaded.url : undefined;
}

function PatchThumbnail({
  featuredPhotoId,
  featuredPhotoTitle,
  tone,
}: {
  featuredPhotoId?: string;
  featuredPhotoTitle?: string;
  tone: string;
}) {
  const source = useAttachmentUrl(featuredPhotoId);
  return (
    <div
      aria-hidden="true"
      className={`patch-thumbnail tone-${tone}${source ? " has-photo" : ""}`}
      title={source ? `Featured photo: ${featuredPhotoTitle ?? "Patch reference"}` : undefined}
    >
      <span />
      <span />
      <span />
      <span />
      <i />
      {source && <img alt="" src={source} />}
    </div>
  );
}

function PatchCardFeaturedAudio({
  attachmentId,
  duration: documentedDuration,
  title,
}: {
  attachmentId: string;
  duration?: number;
  title: string;
}) {
  const source = useAttachmentUrl(attachmentId);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(documentedDuration ?? 0);
  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || !source) return;
    if (audio.paused) {
      document
        .querySelectorAll<HTMLAudioElement>(".patch-card-featured-audio audio")
        .forEach((other) => {
          if (other !== audio) other.pause();
        });
      try {
        await audio.play();
      } catch {
        setPlaying(false);
      }
    } else {
      audio.pause();
    }
  };

  return (
    <div aria-label={`Featured audio: ${title}`} className="patch-card-featured-audio" role="group">
      <audio
        onDurationChange={(event) => {
          if (Number.isFinite(event.currentTarget.duration))
            setDuration(event.currentTarget.duration);
        }}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        preload="metadata"
        ref={audioRef}
        src={source}
      />
      <button
        aria-label={`${playing ? "Pause" : "Play"} featured audio ${title}`}
        disabled={!source}
        onClick={() => void togglePlayback()}
        type="button"
      >
        <Icon name="audio" size={14} />
      </button>
      <span>
        <strong>{title}</strong>
        <small>
          {playing ? "Playing locally" : source ? "Featured sample" : "Loading sample…"}
        </small>
      </span>
      <time>{formatMediaTime(currentTime)}</time>
      <input
        aria-label={`Seek featured audio ${title}`}
        disabled={!duration}
        max={duration || 1}
        min="0"
        onChange={(event) => {
          const nextTime = Number(event.target.value);
          if (audioRef.current) audioRef.current.currentTime = nextTime;
          setCurrentTime(nextTime);
        }}
        step="0.01"
        type="range"
        value={Math.min(currentTime, duration || 1)}
      />
    </div>
  );
}

function formatMediaTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  return `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0")}`;
}

function formatPatchDate(value: string) {
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return `Today, ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }
  return date.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

function LibraryPanel({ onNewPatch }: { onNewPatch: () => void }) {
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [instrument, setInstrument] = useState("all");
  const [category, setCategory] = useState("all");
  const [tag, setTag] = useState("all");
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const [documentationFilter, setDocumentationFilter] = useState<
    "all" | "notes" | "photos" | "audio"
  >("all");
  const [sort, setSort] = useState<"modified" | "title">("modified");
  const filterShellRef = useRef<HTMLDivElement>(null);
  const filterToggleRef = useRef<HTMLButtonElement>(null);
  const {
    activeDocument,
    deletePatch,
    duplicatePatch,
    leftPanelOpen,
    libraryError,
    libraryOperation,
    openPatch,
    patchLibrary,
    toggleLeftPanel,
  } = useEditorStore();
  const instruments = [...new Set(patchLibrary.map((patch) => patch.instrument))].sort();
  const categories = [
    ...new Set(
      patchLibrary.map((patch) => patch.category).filter((item): item is string => !!item),
    ),
  ].sort();
  const tags = [...new Set(patchLibrary.flatMap((patch) => patch.tags))].sort();
  const activeFilterCount =
    Number(instrument !== "all") +
    Number(category !== "all") +
    Number(tag !== "all") +
    Number(favouritesOnly) +
    Number(documentationFilter !== "all");
  const clearFilters = () => {
    setInstrument("all");
    setCategory("all");
    setTag("all");
    setFavouritesOnly(false);
    setDocumentationFilter("all");
  };

  useEffect(() => {
    if (!filtersOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!filterShellRef.current?.contains(event.target as Node)) {
        setFiltersOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
        filterToggleRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filtersOpen]);
  const filteredPatches = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return patchLibrary
      .filter((patch) => {
        const searchText = [
          patch.title,
          patch.author,
          patch.instrument,
          patch.category,
          patch.description,
          patch.documentationText,
          ...patch.tags,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase();
        return (
          (!query || searchText.includes(query)) &&
          (instrument === "all" || patch.instrument === instrument) &&
          (category === "all" || patch.category === category) &&
          (tag === "all" || patch.tags.includes(tag)) &&
          (documentationFilter === "all" ||
            (documentationFilter === "notes" && patch.hasNotes) ||
            (documentationFilter === "photos" && patch.photoCount > 0) ||
            (documentationFilter === "audio" && patch.audioCount > 0)) &&
          (!favouritesOnly || patch.favourite)
        );
      })
      .sort((a, b) =>
        sort === "title"
          ? a.title.localeCompare(b.title)
          : Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
      );
  }, [category, documentationFilter, favouritesOnly, instrument, patchLibrary, search, sort, tag]);

  return (
    <aside aria-label="Patch library" className={`library-panel${leftPanelOpen ? " is-open" : ""}`}>
      <div className="panel-heading">
        <div>
          <h2>Patch library</h2>
        </div>
        <button
          aria-label="Close library"
          className="icon-button panel-close"
          onClick={toggleLeftPanel}
          type="button"
        >
          <Icon name="close" size={16} />
        </button>
      </div>

      <div className="library-query-shell" ref={filterShellRef}>
        <div className="library-query-row">
          <div className="library-search">
            <Icon name="search" size={15} />
            <input
              aria-label="Search patches"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search patches…"
              type="search"
              value={search}
            />
            {search && (
              <button aria-label="Clear search" onClick={() => setSearch("")} type="button">
                <Icon name="close" size={14} />
              </button>
            )}
          </div>
          <button
            aria-controls="library-filter-panel"
            aria-expanded={filtersOpen}
            className={`library-filter-toggle${filtersOpen ? " is-open" : ""}${
              activeFilterCount ? " has-active-filters" : ""
            }`}
            onClick={() => setFiltersOpen((value) => !value)}
            ref={filterToggleRef}
            type="button"
          >
            <Icon name="filter" size={15} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <b aria-label={`${activeFilterCount} active filters`}>{activeFilterCount}</b>
            )}
          </button>
        </div>

        {filtersOpen && (
          <div
            aria-label="Patch filters"
            className="library-filter-popover"
            id="library-filter-panel"
            role="region"
          >
            <div className="library-filter-popover-heading">
              <strong>Filter patches</strong>
              <div>
                <button disabled={activeFilterCount === 0} onClick={clearFilters} type="button">
                  Clear all
                </button>
                <button
                  aria-label="Close filters"
                  className="icon-button"
                  onClick={() => {
                    setFiltersOpen(false);
                    filterToggleRef.current?.focus();
                  }}
                  type="button"
                >
                  <Icon name="close" size={15} />
                </button>
              </div>
            </div>

            <div className="filter-row">
              <select
                aria-label="Filter by instrument"
                onChange={(event) => setInstrument(event.target.value)}
                value={instrument}
              >
                <option value="all">All instruments</option>
                {instruments.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
              <select
                aria-label="Filter by category"
                onChange={(event) => setCategory(event.target.value)}
                value={category}
              >
                <option value="all">All categories</option>
                {categories.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <section className="library-section tag-section">
              <div className="section-heading">
                <span>Quick filters</span>
                <Icon name="chevron" size={13} />
              </div>
              <div className="tag-cloud">
                <button
                  className={tag === "all" ? "is-active" : ""}
                  onClick={() => setTag("all")}
                  type="button"
                >
                  All
                </button>
                {tags.map((item) => (
                  <button
                    className={tag === item ? "is-active" : ""}
                    key={item}
                    onClick={() => setTag(item)}
                    type="button"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </section>

            <section className="library-section collection-list">
              <div className="section-heading">
                <span>Collections</span>
                <Icon name="chevron" size={13} />
              </div>
              <button
                aria-pressed={favouritesOnly}
                className={favouritesOnly ? "is-active" : ""}
                onClick={() => setFavouritesOnly((value) => !value)}
                type="button"
              >
                <Icon name="star" size={14} />
                <span>Favourites</span>
                <b>{patchLibrary.filter((patch) => patch.favourite).length}</b>
              </button>
              <button
                onClick={() => {
                  setSearch("");
                  setInstrument("all");
                  setCategory("all");
                  setTag("all");
                  setFavouritesOnly(false);
                  setDocumentationFilter("all");
                }}
                type="button"
              >
                <Icon name="save" size={14} />
                <span>My patches</span>
                <b>{patchLibrary.length}</b>
              </button>
              <button
                onClick={() => {
                  setSort("modified");
                  setFavouritesOnly(false);
                  setDocumentationFilter("all");
                }}
                type="button"
              >
                <Icon name="list" size={14} />
                <span>Recently modified</span>
                <b>{patchLibrary.length}</b>
              </button>
            </section>

            <section className="library-section collection-list documentation-filters">
              <div className="section-heading">
                <span>Documentation</span>
                <Icon name="chevron" size={13} />
              </div>
              {(
                [
                  ["notes", "Notes", "note", patchLibrary.filter((patch) => patch.hasNotes).length],
                  [
                    "photos",
                    "Photos",
                    "photo",
                    patchLibrary.filter((patch) => patch.photoCount > 0).length,
                  ],
                  [
                    "audio",
                    "Audio",
                    "audio",
                    patchLibrary.filter((patch) => patch.audioCount > 0).length,
                  ],
                ] as const
              ).map(([value, label, icon, count]) => (
                <button
                  aria-pressed={documentationFilter === value}
                  className={documentationFilter === value ? "is-active" : ""}
                  key={value}
                  onClick={() =>
                    setDocumentationFilter((current) => (current === value ? "all" : value))
                  }
                  type="button"
                >
                  <Icon name={icon} size={14} />
                  <span>{label}</span>
                  <b>{count}</b>
                </button>
              ))}
            </section>
          </div>
        )}
      </div>

      <section className="patch-results">
        <div className="results-heading">
          <span>
            Patches <b>{filteredPatches.length}</b>
          </span>
          <button
            onClick={() => setSort((value) => (value === "modified" ? "title" : "modified"))}
            type="button"
          >
            {sort === "modified" ? "Last modified" : "Title"} <Icon name="chevron" size={12} />
          </button>
        </div>
        <div className="patch-card-list">
          {filteredPatches.map((patch, index) => (
            <article
              className={`patch-card${patch.id === activeDocument.id ? " is-selected" : ""}`}
              key={patch.id}
            >
              <button
                className="patch-card-open"
                disabled={Boolean(libraryOperation)}
                onClick={() => void openPatch(patch.id)}
                type="button"
              >
                <PatchThumbnail
                  featuredPhotoId={patch.featuredPhotoId}
                  featuredPhotoTitle={patch.featuredPhotoTitle}
                  tone={["green", "blue", "orange", "violet", "cyan", "amber"][index % 6]!}
                />
                <span className="patch-card-copy">
                  <strong>{patch.title}</strong>
                  <small>
                    {patch.instrument}
                    {patch.category ? ` · ${patch.category}` : ""}
                  </small>
                  <time dateTime={patch.updatedAt}>{formatPatchDate(patch.updatedAt)}</time>
                  {(patch.hasNotes || patch.photoCount > 0 || patch.audioCount > 0) && (
                    <span
                      aria-label={[
                        patch.hasNotes ? "Has notes" : "",
                        patch.photoCount ? `${patch.photoCount} photos` : "",
                        patch.audioCount ? `${patch.audioCount} audio samples` : "",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                      className="patch-card-assets"
                    >
                      {patch.hasNotes && (
                        <i title="Has notes">
                          <Icon name="note" size={12} />
                        </i>
                      )}
                      {patch.photoCount > 0 && (
                        <i title={`${patch.photoCount} photos`}>
                          <Icon name="photo" size={12} />
                          {patch.photoCount}
                        </i>
                      )}
                      {patch.audioCount > 0 && (
                        <i title={`${patch.audioCount} audio samples`}>
                          <Icon name="audio" size={12} />
                          {patch.audioCount}
                        </i>
                      )}
                    </span>
                  )}
                </span>
                <span className="patch-card-meta">
                  {patch.favourite && <Icon name="star" size={14} />}
                  <small>{patch.cableCount}</small>
                </span>
              </button>
              {patch.featuredAudioId && (
                <PatchCardFeaturedAudio
                  attachmentId={patch.featuredAudioId}
                  duration={patch.featuredAudioDuration}
                  key={patch.featuredAudioId}
                  title={patch.featuredAudioTitle || "Featured sample"}
                />
              )}
              <div className="patch-card-actions">
                <button
                  aria-label={`Duplicate ${patch.title}`}
                  disabled={Boolean(libraryOperation)}
                  onClick={() => void duplicatePatch(patch.id)}
                  type="button"
                >
                  <Icon name="plus" size={13} /> Copy
                </button>
                <button
                  aria-label={`Delete ${patch.title}`}
                  disabled={Boolean(libraryOperation)}
                  onClick={() => {
                    if (window.confirm(`Delete “${patch.title}”? This cannot be undone.`)) {
                      void deletePatch(patch.id);
                    }
                  }}
                  type="button"
                >
                  <Icon name="trash" size={13} /> Delete
                </button>
              </div>
            </article>
          ))}
          {filteredPatches.length === 0 && (
            <div className="library-empty">
              <strong>No matching patches</strong>
              <span>Try clearing a filter or create a new patch.</span>
            </div>
          )}
          {libraryError && (
            <div className="library-error" role="alert">
              {libraryError}
            </div>
          )}
        </div>
      </section>

      <div className="library-footer">
        <span>
          {patchLibrary.length} {patchLibrary.length === 1 ? "patch" : "patches"}
        </span>
        <button onClick={onNewPatch} type="button">
          <Icon name="plus" size={15} /> New patch
        </button>
      </div>
    </aside>
  );
}

const toolItems: { id: ToolMode; label: string; icon: IconName; shortcut: string }[] = [
  { id: "select", label: "Select", icon: "pointer", shortcut: "V" },
  { id: "patch", label: "Patch cable", icon: "cable", shortcut: "P" },
  { id: "pan", label: "Pan", icon: "pan", shortcut: "Space" },
  { id: "help", label: "Help mode", icon: "help", shortcut: "H" },
];

function ToolRail() {
  const { canRedo, canUndo, redo, setTool, tool, undo } = useEditorStore();
  return (
    <div aria-label="Workspace tools" className="tool-rail" role="toolbar">
      {toolItems.map((item) => (
        <button
          aria-label={`${item.label} (${item.shortcut})`}
          aria-pressed={tool === item.id}
          className={tool === item.id ? "is-active" : ""}
          data-tooltip={item.label}
          key={item.id}
          onClick={() => setTool(item.id)}
          type="button"
        >
          <Icon name={item.icon} size={18} />
        </button>
      ))}
      <span className="tool-divider" />
      <button
        aria-label="Undo"
        data-tooltip="Undo"
        disabled={!canUndo}
        onClick={undo}
        type="button"
      >
        <Icon name="undo" size={18} />
      </button>
      <button
        aria-label="Redo"
        data-tooltip="Redo"
        disabled={!canRedo}
        onClick={redo}
        type="button"
      >
        <Icon name="redo" size={18} />
      </button>
    </div>
  );
}

const viewModes: { id: ViewMode; label: string }[] = [
  { id: "panel", label: "Panel" },
  { id: "normalled", label: "Normals" },
  { id: "flow", label: "Flow" },
  { id: "list", label: "List" },
];

function WorkspaceHeader() {
  const {
    activeDocument,
    cables,
    focusedSectionId,
    focusSection,
    viewMode,
    setViewMode,
    leftPanelOpen,
    inspectorOpen,
    toggleLeftPanel,
    toggleInspector,
  } = useEditorStore();
  const instrument = useMemo(
    () => compiledInstrumentDefinitionForDocument(activeDocument),
    [activeDocument],
  );
  return (
    <div className="workspace-header">
      <div className="workspace-primary-row">
        <div className="workspace-title">
          <div className="mobile-workspace-panels">
            <button aria-label="Toggle library" onClick={toggleLeftPanel} type="button">
              <Icon name="panelLeft" size={16} />
              <span>{leftPanelOpen ? "Hide" : "Library"}</span>
            </button>
          </div>
          <span className="eyebrow">Patch / {instrument.displayName}</span>
          <h1>
            {activeDocument.title}{" "}
            <button aria-label="Favourite patch" type="button">
              <Icon name="star" size={15} />
            </button>
          </h1>
        </div>
        <div className="patch-facts">
          <span>
            <small>Instrument</small>
            <b>{instrument.displayName}</b>
          </span>
          <span>
            <small>Tempo</small>
            <b>{(activeDocument.metadata.tempo ?? 120).toFixed(1)} BPM</b>
          </span>
          <span>
            <small>Cables</small>
            <b className="cable-count">{cables.length} / 32</b>
          </span>
        </div>
        <div aria-label="Workspace view" className="segmented-control" role="group">
          {viewModes.map((mode) => (
            <button
              aria-pressed={viewMode === mode.id}
              className={viewMode === mode.id ? "is-active" : ""}
              key={mode.id}
              onClick={() => setViewMode(mode.id)}
              type="button"
            >
              {mode.label}
            </button>
          ))}
        </div>
        <button
          aria-label="Toggle inspector"
          className="workspace-inspector-toggle"
          onClick={toggleInspector}
          type="button"
        >
          <Icon name="panelRight" size={16} />
          <span>{inspectorOpen ? "Hide" : "Inspector"}</span>
        </button>
      </div>
      <nav aria-label="Instrument sections" className="section-tabs">
        <button
          aria-pressed={focusedSectionId === null}
          className={focusedSectionId === null ? "is-active" : ""}
          onClick={() => focusSection(null)}
          type="button"
        >
          Full panel
        </button>
        {instrument.sections
          .filter((section) => !section.kind.startsWith("rear"))
          .map((section) => (
            <button
              aria-pressed={focusedSectionId === section.id}
              className={focusedSectionId === section.id ? "is-active" : ""}
              key={section.id}
              onClick={() => focusSection(section.id)}
              type="button"
            >
              {section.shortLabel}
            </button>
          ))}
        <button
          aria-pressed={focusedSectionId === "rear-panel"}
          className={focusedSectionId === "rear-panel" ? "is-active" : ""}
          onClick={() => focusSection("rear-panel")}
          type="button"
        >
          Rear I/O
        </button>
      </nav>
    </div>
  );
}

function EditorWorkspace() {
  const recallOpen = useEditorStore((state) => state.recallOpen);
  const viewMode = useEditorStore((state) => state.viewMode);
  const activePatchId = useEditorStore((state) => state.activeDocument.id);
  return (
    <main className="editor-workspace">
      <WorkspaceHeader />
      <div className={`editor-body${recallOpen ? " has-recall" : ""}`}>
        <div className={`canvas-stage${viewMode === "list" ? " is-list-view" : ""}`}>
          {viewMode === "list" ? (
            <PatchListView />
          ) : (
            <>
              <WorkspaceCanvas />
              <ToolRail />
              <CableManager />
              <ExternalEndpointManager />
              <UtilityManager />
            </>
          )}
        </div>
        <RecallDock key={activePatchId} />
      </div>
    </main>
  );
}

function PatchDetailsEditor({
  document,
  onCancel,
  onSave,
}: {
  document: PatchDocument;
  onCancel: () => void;
  onSave: (update: PatchMetadataUpdate) => void;
}) {
  const [title, setTitle] = useState(document.title);
  const [author, setAuthor] = useState(document.author ?? "");
  const [category, setCategory] = useState(document.metadata.category ?? "");
  const [tempo, setTempo] = useState(String(document.metadata.tempo ?? ""));
  const [tags, setTags] = useState(document.metadata.tags.join(", "));
  const [description, setDescription] = useState(document.metadata.description);
  const [favourite, setFavourite] = useState(document.metadata.favourite);

  return (
    <form
      className="patch-editor-form"
      onSubmit={(event) => {
        event.preventDefault();
        const parsedTempo = Number.parseFloat(tempo);
        onSave({
          title,
          author,
          category,
          tempo: Number.isFinite(parsedTempo) && parsedTempo > 0 ? parsedTempo : undefined,
          tags: tags.split(","),
          description,
          favourite,
        });
      }}
    >
      <div className="patch-editor-heading">
        <div>
          <span className="object-type">Patch document</span>
          <h3>Edit details</h3>
        </div>
        <label className="favourite-toggle">
          <input
            checked={favourite}
            onChange={(event) => setFavourite(event.target.checked)}
            type="checkbox"
          />
          Favourite
        </label>
      </div>
      <label>
        <span>Title</span>
        <input
          autoFocus
          onChange={(event) => setTitle(event.target.value)}
          required
          value={title}
        />
      </label>
      <div className="patch-editor-grid">
        <label>
          <span>Author</span>
          <input onChange={(event) => setAuthor(event.target.value)} value={author} />
        </label>
        <label>
          <span>Tempo</span>
          <input
            inputMode="decimal"
            min="1"
            onChange={(event) => setTempo(event.target.value)}
            step="0.1"
            type="number"
            value={tempo}
          />
        </label>
      </div>
      <label>
        <span>Category</span>
        <input onChange={(event) => setCategory(event.target.value)} value={category} />
      </label>
      <label>
        <span>
          Tags <small>comma separated</small>
        </span>
        <input onChange={(event) => setTags(event.target.value)} value={tags} />
      </label>
      <label>
        <span>Description</span>
        <textarea
          onChange={(event) => setDescription(event.target.value)}
          rows={5}
          value={description}
        />
      </label>
      <div className="patch-editor-actions">
        <button onClick={onCancel} type="button">
          Cancel
        </button>
        <button className="is-primary" type="submit">
          Save details
        </button>
      </div>
    </form>
  );
}

function InspectorPanel() {
  const [editingPatchId, setEditingPatchId] = useState<string | null>(null);
  const {
    activeDocument,
    cables,
    controlValues,
    deleteSelectedCable,
    inspectorTab,
    inspectorOpen,
    selectedId,
    setInspectorTab,
    setViewMode,
    toggleInspector,
    updatePatchMetadata,
  } = useEditorStore();
  const instrument = useMemo(
    () => compiledInstrumentDefinitionForDocument(activeDocument),
    [activeDocument],
  );
  const editing = editingPatchId === activeDocument.id;
  const selectedControl = instrument.controls.find((control) => control.id === selectedId);
  const selectedPort = instrument.ports.find((port) => port.id === selectedId);
  const selectedCable = cables.find((cable) => cable.id === selectedId);
  const normalStates = useMemo(
    () => getNormalledRouteStates(instrument, cables, controlValues),
    [cables, controlValues, instrument],
  );
  const selectedNormal = normalStates.find((state) => state.route.id === selectedId);
  const selectedInternal = instrument.signalConnections.find((route) => route.id === selectedId);
  const signalTrace = useMemo(
    () => traceSignal(instrument, cables, selectedId, controlValues),
    [cables, controlValues, instrument, selectedId],
  );
  const allWarnings = useMemo(() => validateSignalPatch(instrument, cables), [cables, instrument]);
  const relevantWarnings = allWarnings.filter((warning) =>
    selectedCable
      ? warning.cableId === selectedCable.id
      : selectedPort
        ? warning.portIds.includes(selectedPort.id)
        : true,
  );
  const selectedLabel =
    selectedControl?.label ??
    selectedPort?.label ??
    selectedNormal?.route.label ??
    selectedInternal?.label ??
    (selectedCable
      ? `Cable ${selectedCable.number}${selectedCable.label ? ` — ${selectedCable.label}` : ""}`
      : "Patch");
  const linkedAnnotations = activeDocument.workspace.annotations.filter((annotation) => {
    if (selectedControl && annotation.target.kind === "control") {
      return annotation.target.controlId === selectedControl.id;
    }
    if (selectedPort && annotation.target.kind === "port") {
      return annotation.target.portId === selectedPort.id;
    }
    if (selectedCable && annotation.target.kind === "cable") {
      return annotation.target.cableId === selectedCable.id;
    }
    return (
      !selectedControl && !selectedPort && !selectedCable && annotation.target.kind === "patch"
    );
  });
  const selectedSection = instrument.sections.find(
    (section) => section.id === (selectedControl?.sectionId ?? selectedPort?.sectionId),
  );
  const selectedValue = selectedControl
    ? (controlValues[selectedControl.id] ?? selectedControl.defaultValue)
    : null;
  const selectedOption =
    selectedControl?.options && selectedValue !== null
      ? selectedControl.options[Math.round(selectedValue * (selectedControl.options.length - 1))]
      : null;
  const selectedControlType =
    selectedControl?.type === "rotary-selector"
      ? "Rotary selector"
      : selectedControl?.type === "lever"
        ? "Lever switch"
        : selectedControl?.type === "button"
          ? "Button"
          : selectedControl
            ? "Rotary control"
            : null;
  const portElectrical = selectedPort?.electrical
    ? [
        selectedPort.electrical.impedance,
        selectedPort.electrical.range,
        selectedPort.electrical.coupling ? `${selectedPort.electrical.coupling} coupled` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <aside aria-label="Inspector" className={`inspector-panel${inspectorOpen ? " is-open" : ""}`}>
      <div className="panel-heading inspector-heading">
        <div>
          <span className="eyebrow">Inspector</span>
          <h2>{selectedLabel}</h2>
        </div>
        <button
          aria-label="Close inspector"
          className="icon-button panel-close"
          onClick={toggleInspector}
          type="button"
        >
          <Icon name="close" size={16} />
        </button>
      </div>
      <div className="inspector-tabs" role="tablist">
        {["info", "notes", "photos", "audio"].map((item) => (
          <button
            aria-selected={inspectorTab === item}
            className={inspectorTab === item ? "is-active" : ""}
            key={item}
            onClick={() => setInspectorTab(item as "info" | "notes" | "photos" | "audio")}
            role="tab"
            type="button"
          >
            {item}
          </button>
        ))}
      </div>

      <div className="inspector-scroll">
        {inspectorTab === "info" ? (
          <>
            <section className="inspector-card selected-object-card">
              <span className="object-type">
                {selectedControl
                  ? selectedControlType
                  : selectedPort
                    ? `${selectedPort.location ?? "front"} · ${selectedPort.signalType} ${selectedPort.direction}`
                    : selectedCable
                      ? "Patch cable"
                      : selectedNormal
                        ? `Internal normal · ${selectedNormal.status}`
                        : selectedInternal
                          ? "Fixed internal path"
                          : "Patch document"}
              </span>
              <h3>{selectedLabel}</h3>
              {selectedSection && (
                <p>
                  {selectedSection.label} section · {instrument.displayName}
                </p>
              )}
              {selectedNormal && (
                <p>
                  {selectedNormal.status === "active"
                    ? "Signal is following the factory internal route."
                    : `Normal is interrupted by ${selectedNormal.brokenByCableIds.length} patch cable${selectedNormal.brokenByCableIds.length === 1 ? "" : "s"}.`}
                </p>
              )}
              {selectedInternal && <p>{selectedInternal.description}</p>}
              {selectedValue !== null && (
                <div className="inspector-value">
                  <span>Current position</span>
                  <strong>{selectedOption?.label ?? `${Math.round(selectedValue * 100)}%`}</strong>
                  <div>
                    <i style={{ width: `${selectedValue * 100}%` }} />
                  </div>
                </div>
              )}
              {(selectedCable || linkedAnnotations.length > 0) && (
                <div className="selected-object-actions">
                  {linkedAnnotations.length > 0 && (
                    <button onClick={() => setInspectorTab("notes")} type="button">
                      <Icon name="note" size={14} />
                      {linkedAnnotations.length} linked note
                      {linkedAnnotations.length === 1 ? "" : "s"}
                    </button>
                  )}
                  {selectedCable && (
                    <button className="is-danger" onClick={deleteSelectedCable} type="button">
                      <Icon name="trash" size={14} /> Delete cable {selectedCable.number}
                    </button>
                  )}
                </div>
              )}
            </section>

            {editing ? (
              <PatchDetailsEditor
                document={activeDocument}
                key={activeDocument.id}
                onCancel={() => setEditingPatchId(null)}
                onSave={(update) => {
                  updatePatchMetadata(update);
                  setEditingPatchId(null);
                }}
              />
            ) : (
              <>
                <section className="inspector-card patch-details">
                  <div className="card-heading">
                    <h3>Patch details</h3>
                    <button onClick={() => setEditingPatchId(activeDocument.id)} type="button">
                      Edit
                    </button>
                  </div>
                  <dl>
                    <div>
                      <dt>Title</dt>
                      <dd>{activeDocument.title}</dd>
                    </div>
                    <div>
                      <dt>Author</dt>
                      <dd>{activeDocument.author || "—"}</dd>
                    </div>
                    <div>
                      <dt>Category</dt>
                      <dd>{activeDocument.metadata.category || "—"}</dd>
                    </div>
                    <div>
                      <dt>Tempo</dt>
                      <dd>
                        {activeDocument.metadata.tempo
                          ? `${activeDocument.metadata.tempo.toFixed(1)} BPM`
                          : "—"}
                      </dd>
                    </div>
                    <div>
                      <dt>Modified</dt>
                      <dd>{new Date(activeDocument.updatedAt).toLocaleString()}</dd>
                    </div>
                  </dl>
                  <div className="inspector-tags">
                    {activeDocument.metadata.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                    <button
                      aria-label="Add tag"
                      onClick={() => setEditingPatchId(activeDocument.id)}
                      type="button"
                    >
                      <Icon name="plus" size={12} />
                    </button>
                  </div>
                </section>

                <section className="inspector-card description-card">
                  <div className="card-heading">
                    <h3>Description</h3>
                  </div>
                  <p>{activeDocument.metadata.description || "No description yet."}</p>
                </section>
              </>
            )}

            <section className="inspector-card quick-help">
              <div className="card-heading">
                <h3>Quick help</h3>
                <Icon name="help" size={15} />
              </div>
              <strong>{selectedLabel}</strong>
              <p>
                {selectedPort
                  ? (selectedPort.description ??
                    `This ${selectedPort.signalType} ${selectedPort.direction} can be selected as a cable endpoint.`)
                  : selectedNormal
                    ? selectedNormal.route.description
                    : selectedInternal
                      ? selectedInternal.description
                      : selectedCable
                        ? "This physical patch lead overrides any destination normal and participates in signal tracing."
                        : "Drag vertically to adjust. Hold Shift for fine control, use the arrow keys, or press Home to return to the documented default."}
              </p>
              {selectedPort?.normalledFrom && (
                <small className="port-spec">Normalled from: {selectedPort.normalledFrom}</small>
              )}
              {portElectrical && <small className="port-spec">{portElectrical}</small>}
              <button type="button">Open full documentation →</button>
            </section>

            {signalTrace.active && (
              <section className="inspector-card signal-trace-card">
                <div className="card-heading">
                  <h3>Signal trace</h3>
                  <span>{signalTrace.nodeIds.size} points</span>
                </div>
                <p>
                  {signalTrace.upstreamNodeIds.size} upstream · {signalTrace.downstreamNodeIds.size}{" "}
                  downstream · {signalTrace.cableIds.size} patch cables
                </p>
                <button onClick={() => setViewMode("flow")} type="button">
                  Show complete flow →
                </button>
              </section>
            )}

            {relevantWarnings.length > 0 && (
              <section className="inspector-card signal-warning-card">
                <div className="card-heading">
                  <h3>Routing checks</h3>
                  <span>{relevantWarnings.length}</span>
                </div>
                <ul>
                  {relevantWarnings.map((warning) => (
                    <li className={`is-${warning.severity}`} key={warning.id}>
                      {warning.message}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        ) : inspectorTab === "notes" ? (
          <NotesInspector document={activeDocument} key={activeDocument.id} />
        ) : inspectorTab === "photos" ? (
          <AttachmentsInspector document={activeDocument} kind="photo" />
        ) : (
          <AttachmentsInspector document={activeDocument} kind="audio" />
        )}
      </div>
    </aside>
  );
}

function StatusBar() {
  const { lastSavedAt, persistenceError, persistenceStatus } = useEditorStore();
  const statusCopy =
    persistenceStatus === "loading"
      ? "Opening local patch…"
      : persistenceStatus === "unsaved"
        ? "Unsaved local changes"
        : persistenceStatus === "saving"
          ? "Saving locally…"
          : persistenceStatus === "recovered"
            ? "Recovered an unsaved local draft"
            : persistenceStatus === "error"
              ? `Local save error: ${persistenceError ?? "Unknown error"}`
              : "All changes saved locally";
  return (
    <footer className="status-bar">
      <div>
        <span className="ready-dot" /> Ready
      </div>
      <div
        className={`save-state is-${persistenceStatus}`}
        title={lastSavedAt ? `Last saved ${new Date(lastSavedAt).toLocaleString()}` : undefined}
      >
        <Icon name={persistenceStatus === "error" ? "help" : "check"} size={13} /> {statusCopy}
      </div>
      <div className="status-spacer" />
      <div>
        Workspace{" "}
        <button type="button">
          My studio <Icon name="chevron" size={12} />
        </button>
      </div>
    </footer>
  );
}

function NewPatchDialog({ onClose }: { onClose: () => void }) {
  const createNewPatch = useEditorStore((state) => state.createNewPatch);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [
        ...dialogRef.current.querySelectorAll<HTMLElement>("button:not(:disabled)"),
      ];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div className="data-dialog-backdrop" onMouseDown={onClose}>
      <section
        aria-labelledby="new-patch-title"
        aria-modal="true"
        className="data-dialog new-patch-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        ref={dialogRef}
        role="dialog"
      >
        <div className="data-dialog-header">
          <div>
            <span className="eyebrow">New patch</span>
            <h1 id="new-patch-title">Choose an instrument</h1>
          </div>
          <button
            aria-label="Close instrument chooser"
            className="icon-button data-dialog-close"
            onClick={onClose}
            ref={closeRef}
            type="button"
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="data-dialog-body instrument-choice-grid">
          {instrumentCatalog.map(({ document }) => (
            <button
              className="instrument-choice"
              key={document.id}
              onClick={() => {
                createNewPatch(document.id);
                onClose();
              }}
              type="button"
            >
              <span aria-hidden="true" className="instrument-choice-swatch">
                <i />
                <i />
                <i />
                <i />
              </span>
              <strong>{document.metadata.displayName}</strong>
              <small>
                {Object.keys(document.semantics.controls).length} controls ·{" "}
                {Object.keys(document.semantics.ports).length} patch points
              </small>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

export function App() {
  usePatchPersistence();
  const [applicationMode, setApplicationMode] = useState<ApplicationMode>("patch");
  const [designDirty, setDesignDirty] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [dataDialog, setDataDialog] = useState<DataDialogMode | null>(null);
  const [newPatchOpen, setNewPatchOpen] = useState(false);
  const {
    activeDocument,
    leftPanelOpen,
    inspectorOpen,
    setTool,
    setViewMode,
    toggleLeftPanel,
    toggleInspector,
    toggleRecall,
    undo,
    redo,
    deleteSelectedCable,
  } = useEditorStore();

  const confirmDesignNavigation = useCallback(
    () =>
      applicationMode !== "design" ||
      !designDirty ||
      window.confirm("This synth design has unsaved changes. Leave without saving?"),
    [applicationMode, designDirty],
  );

  const changeApplicationMode = useCallback(
    (mode: ApplicationMode) => {
      if (mode === applicationMode) return;
      if (mode === "patch" && !confirmDesignNavigation()) return;
      setApplicationMode(mode);
    },
    [applicationMode, confirmDesignNavigation],
  );

  useEffect(() => {
    void loadApplicationSettings().then(applyApplicationSettings);
  }, []);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, [contenteditable='true']")) return;
      const modifier = event.metaKey || event.ctrlKey;
      if (!modifier && event.key.toLowerCase() === "d") {
        changeApplicationMode(applicationMode === "patch" ? "design" : "patch");
        return;
      }
      if (applicationMode === "design") return;
      if (modifier && event.key.toLowerCase() === "p") {
        event.preventDefault();
        setPrintPreviewOpen(true);
        return;
      }
      if (modifier && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }
      if (modifier && event.key.toLowerCase() === "y") {
        event.preventDefault();
        redo();
        return;
      }
      if (event.key === "Delete" || event.key === "Backspace") deleteSelectedCable();
      if (event.key.toLowerCase() === "v") setTool("select");
      if (event.key.toLowerCase() === "p") setTool("patch");
      if (event.key.toLowerCase() === "h") setTool("help");
      if (event.key.toLowerCase() === "n") setViewMode("normalled");
      if (event.key.toLowerCase() === "l") toggleRecall();
      if (event.key.toLowerCase() === "i") toggleInspector();
      if (event.key === "[") toggleLeftPanel();
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [
    applicationMode,
    changeApplicationMode,
    deleteSelectedCable,
    redo,
    setTool,
    setViewMode,
    toggleInspector,
    toggleLeftPanel,
    toggleRecall,
    undo,
  ]);

  const shellClass = useMemo(
    () =>
      ["application-shell", leftPanelOpen && "has-library", inspectorOpen && "has-inspector"]
        .filter(Boolean)
        .join(" "),
    [leftPanelOpen, inspectorOpen],
  );

  if (printPreviewOpen) {
    return <PrintPreview onClose={() => setPrintPreviewOpen(false)} />;
  }

  const activeInstrument = instrumentBundleForDocument(activeDocument);

  return (
    <div className={`app${applicationMode === "design" ? " is-design-mode" : ""}`}>
      <AppHeader
        applicationMode={applicationMode}
        onExport={() => setDataDialog("export")}
        onImport={() => setDataDialog("import")}
        onModeChange={changeApplicationMode}
        onNavigateAway={confirmDesignNavigation}
        onNewPatch={() => setNewPatchOpen(true)}
        onPrint={() => setPrintPreviewOpen(true)}
        onSettings={() => setDataDialog("settings")}
      />
      {applicationMode === "design" ? (
        <Suspense fallback={<div className="design-studio-loading">Loading Design Studio…</div>}>
          <DesignStudio
            document={activeInstrument.document}
            key={activeInstrument.document.id}
            onDirtyChange={setDesignDirty}
          />
        </Suspense>
      ) : (
        <>
          <div className={shellClass}>
            <LibraryPanel onNewPatch={() => setNewPatchOpen(true)} />
            <EditorWorkspace />
            <InspectorPanel />
          </div>
          <StatusBar />
        </>
      )}
      {dataDialog && (
        <DataPortabilityDialog
          mode={dataDialog}
          onClose={() => setDataDialog(null)}
          onModeChange={setDataDialog}
        />
      )}
      {newPatchOpen && <NewPatchDialog onClose={() => setNewPatchOpen(false)} />}
    </div>
  );
}
